import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { cx } from '@/lib/format';

export type MenuItem =
  | { kind?: 'item'; label: ReactNode; onSelect: () => void; disabled?: boolean; danger?: boolean; icon?: ReactNode; shortcut?: string; title?: string }
  | { kind: 'separator' }
  | { kind: 'label'; label: ReactNode };

/** Positioned popover menu with roving keyboard focus. Used for dropdowns and context menus. */
export function Menu({
  anchor,
  items,
  onClose,
  align = 'start',
}: {
  anchor: { x: number; y: number } | HTMLElement | null;
  items: MenuItem[];
  onClose: () => void;
  align?: 'start' | 'end';
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [pos, setPos] = useState<{ left: number; top: number } | null>(null);
  const [active, setActive] = useState(0);
  const selectable = items.map((it, i) => (it.kind === 'item' || it.kind === undefined) && !it.disabled ? i : -1).filter((i) => i >= 0);

  useLayoutEffect(() => {
    if (!anchor || !ref.current) return;
    const r = ref.current.getBoundingClientRect();
    let left: number;
    let top: number;
    if (anchor instanceof HTMLElement) {
      const a = anchor.getBoundingClientRect();
      left = align === 'end' ? a.right - r.width : a.left;
      top = a.bottom + 4;
    } else {
      left = anchor.x;
      top = anchor.y;
    }
    left = Math.max(4, Math.min(left, window.innerWidth - r.width - 4));
    top = Math.max(4, Math.min(top, window.innerHeight - r.height - 4));
    setPos({ left, top });
    // `items.length`: a menu that grows after opening (entries loaded asynchronously, e.g. download presets) must be
    // fitted again or its tail ends up below the viewport.
  }, [anchor, align, items.length]);

  useEffect(() => {
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      } else if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        e.preventDefault();
        setActive((a) => {
          const idx = selectable.indexOf(a);
          const next = e.key === 'ArrowDown' ? (idx + 1) % selectable.length : (idx - 1 + selectable.length) % selectable.length;
          return selectable[next] ?? a;
        });
      } else if (e.key === 'Enter') {
        const it = items[active];
        if (it && (it.kind === 'item' || it.kind === undefined) && !it.disabled) {
          e.preventDefault();
          it.onSelect();
          onClose();
        }
      }
    };
    document.addEventListener('pointerdown', onDown, true);
    document.addEventListener('keydown', onKey, true);
    return () => {
      document.removeEventListener('pointerdown', onDown, true);
      document.removeEventListener('keydown', onKey, true);
    };
  }, [onClose, items, active, selectable]);

  useEffect(() => {
    setActive(selectable[0] ?? 0);
    ref.current?.focus();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [anchor]);

  if (!anchor) return null;
  return createPortal(
    <div
      ref={ref}
      role="menu"
      tabIndex={-1}
      style={{ left: pos?.left ?? -9999, top: pos?.top ?? -9999 }}
      className="fixed z-[120] max-h-[calc(100vh-8px)] min-w-[180px] overflow-y-auto rounded-lg border border-line bg-bg-elev p-1 text-sm shadow-xl outline-none"
    >
      {items.map((it, i) => {
        if (it.kind === 'separator') return <div key={i} className="my-1 border-t border-line" />;
        if (it.kind === 'label')
          return (
            <div key={i} className="px-2 py-1 text-xs font-medium uppercase tracking-wide text-fg-subtle">
              {it.label}
            </div>
          );
        return (
          <button
            key={i}
            role="menuitem"
            type="button"
            disabled={it.disabled}
            title={it.title}
            onMouseEnter={() => setActive(i)}
            onClick={() => {
              it.onSelect();
              onClose();
            }}
            className={cx(
              'flex w-full items-center gap-2 rounded-md px-2 py-1.5 text-left disabled:opacity-40',
              active === i && 'bg-bg-sunken',
              it.danger && 'text-danger',
            )}
          >
            {it.icon && <span className="w-4 shrink-0 text-fg-muted">{it.icon}</span>}
            <span className="flex-1">{it.label}</span>
            {it.shortcut && <kbd className="ml-4">{it.shortcut}</kbd>}
          </button>
        );
      })}
    </div>,
    document.body,
  );
}
